import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight, Check } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { api } from "@/lib/api";

const navigate = [
  { label: "Services", to: "/services" },
  { label: "About Us", to: "/about" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

const resources = [
  { label: "Refer an Individual", to: "/refer" },
  { label: "For Families", to: "/resources" },
  { label: "For Coordinators", to: "/refer" },
  { label: "Employee Portal", to: "/employee-portal" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await api.post("/newsletter/subscribe", { email });
      setStatus("ok");
      setEmail("");
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("err");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return (
    <footer className="bg-brand-oat text-brand-navy" data-testid="site-footer">
      <div className="wc-container py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
          {/* Brand + contact */}
          <div className="md:col-span-5">
            <BrandLogo className="h-12" />
            <ul className="mt-5 space-y-2 text-sm text-brand-navy/80">
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-brand-orange shrink-0" strokeWidth={1.6} />
                <a href="tel:7579519555" className="hover:text-brand-navy">757-951-9555</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-brand-orange shrink-0" strokeWidth={1.6} />
                <a href="mailto:info@wecareii.com" className="hover:text-brand-navy">info@wecareii.com</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" strokeWidth={1.6} />
                <div>
                  <div className="font-semibold text-brand-navy text-[13px]">Virginia Beach Office</div>
                  <div>3877 Holland Road #508-B, Virginia Beach, VA 23452</div>
                  <div className="mt-1.5 font-semibold text-brand-navy text-[13px]">Eastern Shore Office</div>
                  <div>36304 Lankford Hwy, Belle Haven, VA 23306</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Navigate + Resources */}
          <div className="md:col-span-3 grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-bold">Navigate</h4>
              <ul className="mt-3 space-y-2 text-sm font-medium text-brand-navy/80">
                {navigate.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="hover:text-brand-navy" data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-bold">Resources</h4>
              <ul className="mt-3 space-y-2 text-sm font-medium text-brand-navy/80">
                {resources.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="hover:text-brand-navy" data-testid={`footer-res-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Stay Connected */}
          <div className="md:col-span-4">
            <h4 className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-bold">Stay Connected</h4>
            <p className="mt-3 text-sm text-brand-navy/75 leading-relaxed">
              Occasional updates on services, resources and community events.
            </p>
            <form onSubmit={onSubscribe} className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-full bg-brand-ivory border border-brand-stone/60 placeholder:text-slate-500 text-slate-800 text-sm px-4 py-2.5 outline-none focus:border-brand-navy"
                style={{ transition: "border-color 200ms ease" }}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="newsletter-submit-btn"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-orange text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:brightness-95 disabled:opacity-70"
                style={{ transition: "filter 200ms ease" }}
              >
                {status === "loading" ? "…" : status === "ok" ? <><Check className="w-3.5 h-3.5" /> Sent</> : <>Sign Up <ArrowRight className="w-3.5 h-3.5" /></>}
              </button>
            </form>
            {status === "ok" && (
              <div className="mt-2 text-xs text-emerald-800" data-testid="newsletter-success">
                Thanks — you&apos;re on the list.
              </div>
            )}
            {status === "err" && (
              <div className="mt-2 text-xs text-red-800" data-testid="newsletter-error">
                Something went wrong. Please try again.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* thin warm-neutral divider + slim copyright row */}
      <div className="border-t border-brand-navy/10">
        <div className="wc-container py-3 flex flex-col md:flex-row items-center justify-between gap-1 text-[11px] md:text-xs text-brand-navy/60">
          <div>© {new Date().getFullYear()} WeCare II. All rights reserved.</div>
          <div>Virginia DBHDS-Licensed Provider · HIPAA-Conscious · ADA-Aware</div>
        </div>
      </div>
    </footer>
  );
}
